import { createBrowserRouter, type RouteObject } from 'react-router';
import { ForgotPasswordPage } from '@/features/auth/forgot-password-page';
import { LoginPage } from '@/features/auth/login-page';
import { PasswordLinkPage } from '@/features/auth/password-link-page';
import { RequireArea, StartRedirect } from '@/features/auth/require-auth';
import { AdminOverviewPage } from '@/features/admin/admin-overview-page';
import { AuditPage } from '@/features/admin/audit-page';
import { ProviderPage } from '@/features/admin/provider-page';
import { NotificationsPage } from '@/features/notifications/notifications-page';
import { ProvidersPage } from '@/features/admin/providers-page';
import { ReviewJobPage } from '@/features/admin/review-job-page';
import { ReviewQueuePage } from '@/features/admin/review-queue-page';
import { UserPage } from '@/features/admin/user-page';
import { UsersPage } from '@/features/admin/users-page';
import { ProviderProfilePage } from '@/features/client/provider-profile-page';
import { ClientJobPage } from '@/features/client/client-job-page';
import { ClientJobsPage } from '@/features/client/client-jobs-page';
import { ClientOverviewPage } from '@/features/client/client-overview-page';
import { BillingPage } from '@/features/provider/billing-page';
import { AdminBillingPage } from '@/features/admin/billing-page';
import { FindJobsPage } from '@/features/provider/find-jobs-page';
import { MyProposalsPage } from '@/features/provider/my-proposals-page';
import { ProviderJobPage } from '@/features/provider/provider-job-page';
import { ProviderSignupPage } from '@/features/provider/provider-signup-page';
import { PublicProfilePage } from '@/features/provider/public-profile-page';
import { OnboardingPage } from '@/features/provider/onboarding-page';
import { ProviderOverviewPage } from '@/features/provider/provider-overview-page';
import { SettingsPage } from '@/features/settings/settings-page';
import { ReportPage } from '@/features/admin/report-page';
import { ReportsPage } from '@/features/admin/reports-page';
import { MessagesPage } from '@/features/messages/messages-page';
import { AppShell } from './app-shell';
import type { Area } from './navigation';
import { NotFoundPage } from './not-found-page';

// Every area has its own layout and menu (navigation.ts) and needs a signed-in user with that role.
// Paths here must match the menu paths.
function area(name: Area, children: RouteObject[]): RouteObject {
  return {
    path: `/${name}`,
    element: (
      <RequireArea area={name}>
        <AppShell area={name} />
      </RequireArea>
    ),
    children: [
      ...children,
      { path: 'settings', element: <SettingsPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  };
}

export const router = createBrowserRouter([
  { path: '/', element: <StartRedirect /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/provider-signup', element: <ProviderSignupPage /> },
  // Links from emails: the token is after `#`, so these paths stay the same for both
  { path: '/set-password', element: <PasswordLinkPage /> },
  { path: '/reset-password', element: <PasswordLinkPage /> },
  area('client', [
    { index: true, element: <ClientOverviewPage /> },
    { path: 'jobs', element: <ClientJobsPage /> },
    { path: 'jobs/:id', element: <ClientJobPage /> },
    { path: 'jobs/providers/:id', element: <ProviderProfilePage /> },
    { path: 'messages', element: <MessagesPage /> },
  ]),
  area('provider', [
    { index: true, element: <ProviderOverviewPage /> },
    { path: 'onboarding', element: <OnboardingPage /> },
    { path: 'onboarding/public', element: <PublicProfilePage /> },
    { path: 'jobs', element: <FindJobsPage /> },
    { path: 'jobs/:id', element: <ProviderJobPage /> },
    { path: 'proposals', element: <MyProposalsPage /> },
    { path: 'billing', element: <BillingPage /> },
    { path: 'messages', element: <MessagesPage /> },
  ]),
  area('admin', [
    { index: true, element: <AdminOverviewPage /> },
    { path: 'review', element: <ReviewQueuePage /> },
    { path: 'billing', element: <AdminBillingPage /> },
    { path: 'review/:id', element: <ReviewJobPage /> },
    { path: 'providers', element: <ProvidersPage /> },
    { path: 'providers/:id', element: <ProviderPage /> },
    { path: 'users', element: <UsersPage /> },
    { path: 'users/:id', element: <UserPage /> },
    { path: 'audit', element: <AuditPage /> },
    { path: 'reports', element: <ReportsPage /> },
    { path: 'reports/:id', element: <ReportPage /> },
  ]),
  { path: '*', element: <NotFoundPage standalone /> },
]);
